import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ViewFoodEvent, ViewPostFoodEvent } from '@xeetr/types/models'
import { useContext, useMemo, useState } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { invalidateFoodEventQueries, invalidateTenantQueries } from '../lib/mutationHelpers'
import { queryKeys } from '../lib/queryKeys'
import { FoodEventService } from '../services/FoodEvent.service'

import { useErrorHandler } from './common/useErrorHandler'
import { useTenantOptionalService } from './common/useServiceFactory'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseEventsReturn {
  events: ViewFoodEvent[]
  event: ViewFoodEvent | undefined
  isLoading: boolean
  isError: boolean

  get: (id: string) => void
  create: (event: ViewPostFoodEvent, callback?: () => void) => void
  update: (event: ViewPostFoodEvent & { id: string }, callback?: () => void) => void
  remove: (event: ViewFoodEvent) => void

  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
}

export const useEvents = (): UseEventsReturn => {
  const { tenantId, tenant } = useContext(ActiveTenantContext)

  // Get events from tenant data
  const events = useMemo(() => tenant?.events || [], [tenant?.events])
  const queryClient = useQueryClient()

  const [eventId, setEventId] = useState<string | null>(null)

  const getService = useTenantOptionalService(FoodEventService)

  const eventQuery = useQuery({
    queryKey: tenantId && eventId ? queryKeys.foodEvents.detail(tenantId, eventId) : [],
    queryFn: async () => {
      if (!tenantId || !tenant || !eventId) return undefined
      const service = await getService()
      return service.getFoodEvent(eventId)
    },
    enabled: Boolean(tenantId && eventId),
    staleTime: 5 * 60 * 1000,
  })

  useErrorHandler(eventQuery.isError, 'Fehler beim Laden des Events.')

  const createMutation = useMutation({
    mutationFn: async (foodEvent: ViewPostFoodEvent) => {
      const service = await getService()
      return service.postFoodEvent(foodEvent)
    },
    onSuccess: () => {
      if (!tenantId) return
      invalidateTenantQueries(tenantId)
      invalidateFoodEventQueries(tenantId)

      showToast({ severity: 'success', detail: 'Das Event wurde erfolgreich erstellt!' })
    },
    onError: (error) => {
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  useErrorHandler(eventQuery.isError, 'Fehler beim Laden des Events.')

  const updateMutation = useMutation({
    mutationFn: async (foodEvent: ViewPostFoodEvent & { id: string }) => {
      const service = await getService()
      return service.putFoodEvent(foodEvent)
    },
    onSuccess: (_, variables) => {
      if (!tenantId) return
      queryClient.invalidateQueries({
        queryKey: queryKeys.foodEvents.detail(tenantId, variables.id),
      })
      invalidateTenantQueries(tenantId)
      invalidateFoodEventQueries(tenantId)

      showToast({ severity: 'success', detail: 'Das Event wurde erfolgreich aktualisiert!' })
    },
    onError: (error) => {
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (foodEventId: string) => {
      const service = await getService()
      return service.deleteFoodEvent(foodEventId)
    },
    onSuccess: () => {
      if (!tenantId) return
      setEventId(null)
      invalidateTenantQueries(tenantId)
      invalidateFoodEventQueries(tenantId)
      showToast({
        severity: 'success',
        detail: 'Event erfolgreich gelöscht!',
      })
    },
    onError: (error) => {
      console.error(error)
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  return {
    events,
    event: eventQuery.data,
    isLoading: eventQuery.isLoading || deleteMutation.isPending,
    isError: eventQuery.isError || deleteMutation.isError,

    get: (id: string) => {
      setEventId(id)
    },
    create: (event, callback) => {
      createMutation.mutate(event, { onSuccess: callback })
    },
    update: (event, callback) => {
      updateMutation.mutate(event, { onSuccess: callback })
    },
    remove: (event: ViewFoodEvent) => {
      if (event.id) {
        deleteMutation.mutate(event.id)
      }
    },

    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
